import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import { supabase } from '../lib/supabase';

const ActivateEmployeeModal = ({ isOpen, onClose, employee, onActivateSuccess }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  if (!isOpen || !employee) return null;

  const handleActivate = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profili')
        .update({
          attivo: true,
          activated_at: new Date().toISOString(),
          activation_note: note
        })
        .eq('id', employee.id);

      if (error) throw error;
      
      alert(t('activate_modal.success_activate'));
      if (onActivateSuccess) onActivateSuccess();
      onClose();
    } catch (err) {
      console.error("Greška pri aktivaciji:", err);
      alert(t('common.error') + ": " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString(i18n.language === 'bs' ? 'bs-BA' : i18n.language);
  };

  // Provjera da li podaci uopšte postoje
  const hasHistory = employee.deactivated_at || employee.reason_deactivate;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md">
      <div className="bg-card w-full max-w-md rounded-3xl border border-emerald-500/20 shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
        
        {/* Header */}
        <div className="p-6 border-b border-border bg-emerald-500/5 flex flex-col items-center text-center relative">
          
          {/* Info dugme - Uvijek vidljivo */}
          <button 
            onClick={() => setShowHistory(!showHistory)}
            className={`absolute top-4 right-4 p-2.5 rounded-2xl transition-all shadow-sm border ${
              showHistory 
                ? 'bg-emerald-500 text-white border-emerald-400' 
                : 'bg-background text-muted-foreground border-border hover:border-emerald-500/50 hover:text-emerald-600'
            }`}
          >
            <Icon name="History" size={18} />
          </button>

          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center text-emerald-600 mb-4 shadow-inner">
            <Icon name="UserCheck" size={32} />
          </div>
          <h3 className="font-black text-xl text-foreground uppercase tracking-tight">
            {t('activate_modal.title')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {t('activate_modal.subtitle')}
          </p>
          <div className="mt-2 px-4 py-1 bg-muted rounded-full border border-border font-bold text-foreground">
            {employee.nome} {employee.cognome}
          </div>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          
          {/* Panel za Historiju */}
          {showHistory && (
            <div className="bg-muted/30 border border-border rounded-2xl p-4 animate-in slide-in-from-top-2 duration-200">
              <div className="flex items-center gap-2 mb-3 text-primary">
                <Icon name="Clock" size={14} />
                <span className="text-[10px] font-black uppercase tracking-widest">{t('activate_modal.history_title')}</span>
              </div>

              {hasHistory ? (
                <div className="space-y-3">
                  <div className="flex flex-col gap-1">
                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-tighter">{t('activate_modal.deactivated_on')}</p>
                    <p className="text-xs font-bold text-foreground">{formatDate(employee.deactivated_at)}</p>
                  </div>
                  <div className="flex flex-col gap-1">
                    <p className="text-[9px] font-black text-muted-foreground uppercase tracking-tighter">{t('activate_modal.previous_reason')}</p>
                    <p className="text-xs font-medium italic text-foreground/70 bg-background/50 p-2 rounded-lg border border-border/50">
                      {employee.reason_deactivate || "-"}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="py-2 text-center italic text-muted-foreground text-xs">
                  {t('activate_modal.no_history')}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[10px] font-black uppercase tracking-widest text-muted-foreground ml-1">
              {t('activate_modal.reason_label')}
            </label>
            <textarea
              className="w-full bg-background border border-border rounded-2xl p-4 text-sm font-medium focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/40 outline-none transition-all resize-none min-h-[100px]"
              placeholder={t('activate_modal.placeholder')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          
          <div className="bg-emerald-500/5 p-4 rounded-2xl border border-emerald-500/10 flex gap-3 items-start">
            <Icon name="Info" size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-emerald-700 leading-relaxed font-medium">
              {t('activate_modal.warning_login')}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-muted/10 border-t border-border flex gap-3">
          <button 
            onClick={handleActivate}
            disabled={loading}
            className="flex-[2] flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50"
          >
            {loading ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <><Icon name="UserCheck" size={16} /> {t('activate_modal.confirm_btn')}</>
            )}
          </button>
          
          <button 
            onClick={onClose} 
            className="flex-1 px-6 py-3 bg-muted text-foreground rounded-2xl font-bold text-xs uppercase tracking-widest border border-border hover:bg-muted/80 transition-all"
          >
            {t('activate_modal.cancel_btn')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivateEmployeeModal;